/*
(c) Copyright European Space Agency, 2023

Project name: THEMIS: Tracking the Health of the Environment and Missions in Space

Description:  Module for IndexTotalMissionPhase component of Impact Evaluation Dashboard 
Author: Deimos Space UK

Version: 1.0
*/
import React, { useEffect, useState } from "react";
import { Card, CardBody, Row, Button, Alert, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Col } from "reactstrap";
import Chart from "react-apexcharts";
import { ESA_COLORS } from "../../../../core/models/colors";
import { useParams } from "react-router-dom";
import { getPetition } from "../../../../api/api";
import Spinner from 'react-bootstrap/Spinner';
import { saveFile, rawDataIntoExcel } from "../../../../api/excel-api";

export default function IndexTotalMissionPhase({ spacecraftId, spacecraftIdArray, constellation, missionId }) {
  const { id } = useParams();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [exporting, setExporting] = useState(false)

  const [PMDoptions, setPMDoptions] = useState('')
  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const setDataKyes = () => {
    const keys = [];

    if(constellation && spacecraftId===null){

      if (data?.deployment) {
        keys.push('Deployment')
      }
      if (data?.operational) {
        keys.push('Operational')
      }
      if (data?.decommissioning) {
        keys.push('Decommissioning')
      }
    }else{
    if (data?.launch) {
      keys.push('Satellites:' + data?.number_of_satellites + ' Launch')
    }
    if (data?.injection) {
      keys.push('Satellites:' + data?.number_of_satellites + ' Injection')
    }
    if (data?.operational) {
      keys.push('Satellites:' + data?.number_of_satellites + ' Operational')
    }
    if (PMDoptions !== 'no_disposal') {

      keys.push('Satellites:' + data?.number_of_satellites + ' No PMD')

    } else {
      keys.push('Satellites:' + data?.number_of_satellites + ' No disposal')
    }
    if (PMDoptions === 'targeted_disposal') {
      if (data?.pmd?.targeted_disposal) {
        keys.push('Satellites:' + data?.number_of_satellites + ' Target-time deorbit')
      }
    } else if (PMDoptions === 'direct_disposal') {
      if (data?.pmd?.direct_disposal) {
        keys.push('Satellites:' + data?.number_of_satellites + ' Direct Disposal')
      }
    } else if (PMDoptions === 'reorbit_disposal') {
      if (data?.pmd?.reorbit_disposal) {
        keys.push('Satellites:' + data?.number_of_satellites + ' Target-orbit deorbit')
      }
    }
  }

    return keys
  }
  const getLabels = () => {
    const keys = [];
    if (data?.launch) {
      keys.push('Launch')
    }
    if (data?.injection) {
      keys.push('Injection')
    }

    if (data?.operational) {
      keys.push('Operational')
    }
    if (PMDoptions !== 'no_disposal') {

      keys.push('No PMD')

    } else {
      keys.push('No disposal')
    }
    if (PMDoptions === 'targeted_disposal') {
      if (data?.pmd?.targeted_disposal) {
        keys.push('Target-time deorbit')
      }
    } else if (PMDoptions === 'direct_disposal') {
      if (data?.pmd?.direct_disposal) {
        keys.push('Direct Disposal')
      }
    } else if (PMDoptions === 'reorbit_disposal') {
      if (data?.pmd?.reorbit_disposal) {
        keys.push('Target-orbit deorbit')
      }
    }

    return keys
  }

  const getConstellationLabels = () => {
    const keys = [];
    if (data?.deployment) {
      keys.push('Deployment')
    }
    if (data?.operational) {
      keys.push('Operational')
    }
    if (data?.decommissioning) {
      keys.push('Decommissioning')
    }

    return keys
  }

  useEffect(() => {
    if(data?.pmd?.targeted_disposal){
      setPMDoptions('targeted_disposal')
    }else if(data?.pmd?.direct_disposal){
      setPMDoptions('direct_disposal')
    }else if(data?.pmd?.reorbit_disposal){
      setPMDoptions('reorbit_disposal')
    }else if(data?.pmd?.no_disposal){
      setPMDoptions('no_disposal')
    }
  }, [data])


  useEffect(() => {
    setLoading(true)
    if (spacecraftId === null && spacecraftIdArray[0] && !constellation) {
      getPetition(`/api/debris_index/share_of_debris_index_per_phase_for_spacecraft?spacecraft_id=${spacecraftIdArray[0]}`)
        .then((data) => {
          setData(data)
          setLoading(false)
          setError(false)
        })
        .catch((error) => {
          setError(true)
          setLoading(false)
        })
    } else {
      if (constellation && spacecraftId !== null) {
        getPetition(`/api/debris_index/share_of_debris_index_per_phase_for_spacecraft?spacecraft_id=${spacecraftId}`)
          .then((data) => {
            setData(data)
            setLoading(false)
            setError(false)
          })
          .catch((error) => {
            setError(true)
            setLoading(false)
          })
      } else if (constellation && spacecraftId === null) {
        getPetition(`/api/debris_index/debris_index_per_phase_for_constellation?mission_id=${missionId}`)
          .then((data) => {
            setData(data)
            setLoading(false)
            setError(false)
          })
          .catch((error) => {
            setError(true)
            setLoading(false)
          })
      }

    }
  }, [spacecraftId, id, spacecraftIdArray]);

  const apexDonutOpts = {
    chart: {
      height: 240,
      type: "pie",
    },
    labels: setDataKyes(),
    tooltip: {
      y: {
        formatter: function (val) {
          return val.toExponential(3);
        },

      },
    },

    colors: ESA_COLORS,
    legend: {
      customLegendItems: (constellation && spacecraftId===null) ? getConstellationLabels() : getLabels(),
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      verticalAlign: "middle",
      floating: false,
      fontSize: "14px",
      offsetX: 0,
      offsetY: 10,
    },
    responsive: [
      {
        breakpoint: 600,
        options: {
          chart: {
            height: 240,
          },
          legend: {
            show: false,
          },
        },
      },
    ],
  };
  const chartContainerStyle = {
    width: '100%',
    height: 'auto',
  };
  const getCircularData = () => {
    const circularData = [];
    if (constellation && spacecraftId === null) {     
      circularData.push(data?.deployment || 0)
      circularData.push(data?.operational || 0)
      circularData.push(data?.decommissioning || 0)
    } else {
      circularData.push(data?.launch || 0)
      circularData.push(data?.injection || 0)
      circularData.push(data?.operational || 0)
      if (PMDoptions !== 'no_disposal') {
        circularData.push(data?.no_pmd || 0)
      } else {
        circularData.push(data?.pmd?.no_disposal || 0)
      }
      if (PMDoptions === 'targeted_disposal') {
        circularData.push(data?.pmd?.targeted_disposal || 0)
      } else if (PMDoptions === 'direct_disposal') {
        circularData.push(data?.pmd?.direct_disposal || 0)
      } else if (PMDoptions === 'reorbit_disposal') {
        circularData.push(data?.pmd?.reorbit_disposal || 0)
      }
    }
    return circularData;
  }
  const fetchDataToExcel = () => {
    setExporting(true)
    let excelRows = {
      "Injection": data.injection,
      "Launch": data.launch,
      "Operational": data.operational,
      "No PMD": data.no_pmd,
    }
    if (data.pmd && data.pmd.direct_disposal) {
      excelRows.direct_disposal = data.pmd.direct_disposal
    }
    if (data.pmd && data.pmd.reorbit_disposal) {
      excelRows.reorbit_disposal = data.pmd.reorbit_disposal
    }
    if (data.pmd && data.pmd.targeted_disposal) {
      excelRows.targeted_disposal = data.pmd.targeted_disposal
    }
    if (data.pmd && data.pmd.no_disposal) {
      excelRows.no_disposal = data.pmd.no_disposal
    }

    const excelData = rawDataIntoExcel([excelRows], 'Share of debris index per phase')
    saveFile(excelData, 'Share of debris index per phase')
    setExporting(false)
  }
  const getLabel = (label) => {
    if (label === 'no_disposal') {
      return 'No disposal'
    } else if (label === 'targeted_disposal') {
      return 'Target-time deorbit'
    } else if (label === 'direct_disposal') {
      return 'Direct Disposal'
    } else if (label === 'reorbit_disposal') {
      return 'Target-orbit deorbit'
    }
  }

  return (
    <Card className="widget-flat h-100">
      <CardBody>
        <Row style={{ display: 'flex', alignItems: 'center' }}>
          <h4 style={{ color: '#003247' }} className="header-title float-left col " >SHARE OF DEBRIS INDEX PER PHASE</h4>
          {!error && (!constellation || spacecraftId!==null) &&
            <Col>

              <Dropdown className="float-right" isOpen={dropdownOpen} toggle={toggle} >
                <DropdownToggle style={{ background: "#003247" }} caret>{getLabel(PMDoptions)}</DropdownToggle>
                <DropdownMenu  >
                  <DropdownItem disabled={!data?.pmd?.no_disposal} onClick={() => setPMDoptions('no_disposal')}>No disposal</DropdownItem>
                  <DropdownItem disabled={!data?.pmd?.targeted_disposal} onClick={() => setPMDoptions('targeted_disposal')}>Target-time deorbit</DropdownItem>
                  <DropdownItem disabled={!data?.pmd?.direct_disposal} onClick={() => setPMDoptions('direct_disposal')}>Direct Disposal</DropdownItem>
                  <DropdownItem disabled={!data?.pmd?.reorbit_disposal} onClick={() => setPMDoptions('reorbit_disposal')}>Target-orbit deorbit</DropdownItem>
                </DropdownMenu>
              </Dropdown>

              <Button disabled={exporting} onClick={() => fetchDataToExcel()} style={{ background: '#003247' }} className="float-right"><i className="uil  uil-down-arrow "></i></Button>
            </Col>
          }
        </Row>
        <div className="bar-container " style={chartContainerStyle}>
          {
            error ? (
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '20px', height: 'auto' }}>
                <Alert color="warning">
                  <h4 className="alert-heading">No data available</h4>
                  <p>
                    There is no data available for this mission.
                  </p>
                </Alert>
              </div>
            ) :
              (

                loading ?
                  <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                    <Spinner animation="border" />
                  </div>
                  : <Chart
                    options={apexDonutOpts}
                    series={getCircularData()}
                    type="donut"
                    height={390}
                    className="apex-charts"
                  />

              )

          }

        </div>

      </CardBody>
    </Card>
  );
}
